<script setup lang="ts">
  import { computed, type HTMLAttributes } from 'vue'
  import { IconAiGradient, IconLoading } from '../icons'
  import { cn } from '../../lib/utils'
  import { useAppLoading } from '../app/loading/loading-provider.vue'

  export interface BtnGradientProps {
    class?: HTMLAttributes['class']
    label?: string
    disabled?: boolean
    loading?: boolean
    loadingId?: string[] | string
  }

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<BtnGradientProps>(), {
    disabled: false
  })

  const emit = defineEmits<{
    (e: 'click', event?: MouseEvent): void
  }>()

  const isLoading = computed(() => {
    return props.loading || appIsLoading(props.loadingId)
  })

  const isDisabled = computed(() => {
    return props.disabled || isLoading.value
  })

  const { isLoading: appIsLoading } = useAppLoading()
</script>
<template>
  <div :class="cn('group relative flex w-full', props.class)">
    <!-- glow -->
    <div
      v-if="!isDisabled"
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 -z-10 scale-90 rounded-md bg-[conic-gradient(from_180deg_at_50%_50%,#22c55e,#06b6d4,#3b82f6,#a855f7,#ec4899,#f97316,#22c55e)] opacity-0 blur-lg transition duration-300 group-hover:opacity-25"
    ></div>

    <button
      class="relative isolate flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-md p-[1px] focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60 disabled:pointer-events-none disabled:opacity-50"
      :disabled="isDisabled"
      @click.stop="emit('click', $event)"
    >
      <!-- ring -->
      <div
        v-if="!isDisabled"
        aria-hidden="true"
        class="pointer-events-none absolute left-1/2 top-1/2 h-[1000px] w-[1000px] -translate-x-1/2 -translate-y-1/2 animate-[spin_10s_linear_infinite] rounded-full bg-[conic-gradient(from_180deg_at_50%_50%,#22c55e,#06b6d4,#3b82f6,#a855f7,#ec4899,#f97316,#22c55e)] opacity-75 group-hover:animate-[spin_3s_linear_infinite] group-hover:opacity-100"
      ></div>

      <!-- inner -->
      <div
        class="bg-background text-foreground relative z-10 inline-flex h-8 w-full select-none items-center justify-center gap-1.5 rounded-md px-3 text-xs font-medium ring-1 ring-white/10 transition"
        :class="{
          border: isDisabled,
          'group-hover:bg-accent/95': !isDisabled
        }"
      >
        <span
          class="text-neutral-800 group-hover:animate-[gradient_3s_ease_infinite] group-hover:bg-gradient-to-r group-hover:from-emerald-400 group-hover:via-blue-500 group-hover:to-pink-500 group-hover:bg-[length:200%_200%] group-hover:bg-clip-text group-hover:text-transparent dark:text-neutral-300"
        >
          {{ label }}
        </span>

        <IconAiGradient v-if="!isLoading" class="size-4" />
        <!-- <Icon v-if="!props.loading" v-bind="props.icon" class="size-4" /> -->
        <IconLoading v-if="isLoading" class="size-4" />
      </div>
    </button>
  </div>
</template>
